import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellRing, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { sd } from '@sd/lib/sdPaths';
import {
  ServiceDeskAPI,
  serviceDeskInAppNotificationsQueryKey,
} from '@sd/services/APIs';
import { parseInAppNotifications } from '@sd/lib/inAppNotifications';
import { NotificationListItem } from '@sd/components/notifications/NotificationListItem';
import { toast } from 'sonner';

const PREVIEW_SIZE = 10;

export function NotificationBell() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [locallyReadIds, setLocallyReadIds] = useState<Set<string>>(() => new Set());

  const apiParams = useMemo(
    () => ({ page: 1, page_size: PREVIEW_SIZE }),
    []
  );

  const { data: rawData, isLoading, isError } = useQuery({
    queryKey: [...serviceDeskInAppNotificationsQueryKey, 'bell', apiParams],
    queryFn: () =>
      ServiceDeskAPI.inAppNotifications.list(apiParams).then(r => r.data),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  const items = useMemo(() => {
    return parseInAppNotifications(rawData).map(n => ({
      ...n,
      read: n.read || locallyReadIds.has(n.id),
    }));
  }, [rawData, locallyReadIds]);

  const unreadCount = items.filter(i => !i.read).length;

  const markReadMutation = useMutation({
    mutationFn: (id: string) =>
      ServiceDeskAPI.inAppNotifications.markRead(id).then(r => r.data),
    onMutate: id => {
      setLocallyReadIds(prev => new Set(prev).add(id));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: serviceDeskInAppNotificationsQueryKey,
        exact: false,
      });
    },
    onError: (_err, id) => {
      setLocallyReadIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.error('Could not mark as read. Please try again.');
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () =>
      ServiceDeskAPI.inAppNotifications.markAllRead().then(r => r.data),
    onMutate: () => {
      setLocallyReadIds(prev => {
        const next = new Set(prev);
        items.filter(i => !i.read).forEach(i => next.add(i.id));
        return next;
      });
    },
    onSuccess: () => {
      toast.success('All marked as read');
      void queryClient.invalidateQueries({
        queryKey: serviceDeskInAppNotificationsQueryKey,
        exact: false,
      });
    },
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: serviceDeskInAppNotificationsQueryKey,
        exact: false,
      });
      toast.error('Failed to mark all as read');
    },
  });

  const markAllRead = () => {
    if (unreadCount === 0) return;
    markAllReadMutation.mutate();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', unreadCount > 0 && 'text-primary')}
          title="Notifications"
        >
          {unreadCount > 0 ? (
            <BellRing className="h-5 w-5" />
          ) : (
            <Bell className="h-5 w-5 text-muted-foreground" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground shadow-sm">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[min(92vw,480px)] p-0 bg-popover border-border z-50 shadow-lg overflow-hidden"
      >
        <div className="flex flex-col gap-3 border-b bg-card px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-base font-semibold text-foreground">Notifications</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {unreadCount > 0
                ? `You have ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
                : 'You are all caught up'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 gap-2 self-start sm:self-auto"
            onClick={e => {
              e.preventDefault();
              markAllRead();
            }}
            disabled={unreadCount === 0 || markAllReadMutation.isPending}
          >
            {markAllReadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Mark All Read
          </Button>
        </div>

        <div className="max-h-[min(60vh,480px)] overflow-y-auto space-y-3 bg-muted/20 p-3 sm:p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin opacity-60" />
              <p className="text-sm">Loading notifications…</p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
              <Bell className="h-10 w-10 opacity-40" />
              <p className="font-medium text-foreground">Could not load notifications</p>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
              <Bell className="h-10 w-10 opacity-40" />
              <p className="font-medium text-foreground">No notifications</p>
            </div>
          ) : (
            items.map(n => (
              <NotificationListItem
                key={n.id}
                item={n}
                ticketLinkTo={sd('tickets')}
                isMarking={
                  markReadMutation.isPending && markReadMutation.variables === n.id
                }
                onMarkRead={id => markReadMutation.mutate(id)}
              />
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
